import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { Button, Dropdown } from 'antd';
import { Tooltip } from '@mui/material';
import { SyncOutlined, DownOutlined, FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import './TimeframeChooser.css';

const TimeframeChooser = ({
  selectedTimeframe,
  onTimeframeChange,
  darkMode,
  onRefresh,
  isDisabled = false,
  onFullscreenToggle,
  isFullscreen = false
}) => {
  const timeframes = useMemo(() => ['M1', 'M5', 'M15', 'H1', 'H4', 'D', 'W', 'M'], []);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef(null);
  const [visibleTimeframes, setVisibleTimeframes] = useState(timeframes);
  const [hiddenTimeframes, setHiddenTimeframes] = useState([]);

  // Add theme data attribute for CSS variables
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Responsive timeframe visibility
  useEffect(() => {
    const updateVisibility = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const buttonWidth = 44;
      const reservedSpace = 120; // Space for refresh + fullscreen buttons
      const availableWidth = containerWidth - reservedSpace;
      const maxButtons = Math.max(1, Math.floor(availableWidth / buttonWidth));

      if (maxButtons >= timeframes.length) {
        setVisibleTimeframes(timeframes);
        setHiddenTimeframes([]);
      } else {
        // Always keep selected timeframe visible
        const selectedIndex = timeframes.indexOf(selectedTimeframe);
        let visibleIndices = [selectedIndex];

        // Add buttons around selected
        let before = selectedIndex - 1;
        let after = selectedIndex + 1;

        while (visibleIndices.length < maxButtons && (before >= 0 || after < timeframes.length)) {
          if (before >= 0) visibleIndices.push(before--);
          if (visibleIndices.length < maxButtons && after < timeframes.length) visibleIndices.push(after++);
        }

        visibleIndices.sort((a, b) => a - b);
        setVisibleTimeframes(visibleIndices.map(i => timeframes[i]));
        setHiddenTimeframes(timeframes.filter((_, i) => !visibleIndices.includes(i)));
      }
    };

    updateVisibility();

    const resizeObserver = new ResizeObserver(updateVisibility);
    if (containerRef.current?.parentElement) {
      resizeObserver.observe(containerRef.current.parentElement);
    }

    return () => resizeObserver.disconnect();
  }, [timeframes, selectedTimeframe]);

  const handleRefresh = useCallback(() => {
    if (isRefreshing || isDisabled) return;
    setIsRefreshing(true);
    onRefresh?.();
    setTimeout(() => setIsRefreshing(false), 1000);
  }, [isRefreshing, isDisabled, onRefresh]);

  // Keyboard shortcuts for timeframes
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle if not typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      const keyMap = { '1': 'M1', '2': 'M5', '3': 'M15', '4': 'H1', '5': 'H4', '6': 'D', '7': 'W', '8': 'M' };
      if (keyMap[e.key]) {
        e.preventDefault();
        onTimeframeChange(keyMap[e.key]);
      }
      if (e.key.toLowerCase() === 'r' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        handleRefresh();
      }
      if (e.key.toLowerCase() === 'f' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        onFullscreenToggle?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onTimeframeChange, handleRefresh, onFullscreenToggle]);

  const hiddenTimeframeItems = hiddenTimeframes.map(tf => ({
    key: tf,
    label: (
      <div className={`ntc-timeframe-menu-item ${selectedTimeframe === tf ? 'active' : ''}`}>
        {tf}
      </div>
    ),
    onClick: () => onTimeframeChange(tf),
  }));

  return (
    <div ref={containerRef} className="ntc-simple-toolbar">
      {/* Refresh Button */}
      <Tooltip title="Refresh (R)" arrow placement="bottom">
        <span>
          <Button
            className={`ntc-toolbar-btn ${isDisabled ? 'disabled' : ''}`}
            icon={<SyncOutlined spin={isRefreshing} />}
            onClick={handleRefresh}
            disabled={isDisabled}
          />
        </span>
      </Tooltip>

      {/* Timeframe Pills */}
      <div className="ntc-timeframe-pills">
        {visibleTimeframes.map(tf => (
          <button
            key={tf}
            className={`ntc-timeframe-pill ${selectedTimeframe === tf ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
            onClick={() => !isDisabled && onTimeframeChange(tf)}
            disabled={isDisabled}
          >
            {tf}
          </button>
        ))}

        {/* Hidden timeframes dropdown */}
        {hiddenTimeframes.length > 0 && (
          <Dropdown
            menu={{ items: hiddenTimeframeItems }}
            trigger={['click']}
            placement="bottomRight"
            disabled={isDisabled}
          >
            <button
              className={`ntc-timeframe-pill ntc-more-btn ${hiddenTimeframes.includes(selectedTimeframe) ? 'active' : ''}`}
              disabled={isDisabled}
            >
              {hiddenTimeframes.includes(selectedTimeframe) ? selectedTimeframe : '···'}
              <DownOutlined style={{ fontSize: '10px', marginLeft: '4px' }} />
            </button>
          </Dropdown>
        )}
      </div>

      {/* Fullscreen Toggle */}
      {onFullscreenToggle && (
        <Tooltip title={isFullscreen ? "Exit fullscreen (F)" : "Fullscreen (F)"} arrow placement="bottom">
          <span>
            <Button
              className="ntc-toolbar-btn"
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={onFullscreenToggle}
            />
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default TimeframeChooser;
